import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, RefreshCw, Users, Mail, Search, Save, ChevronDown, ChevronRight, Send, CheckCircle2, Clock, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const StatusPill = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const map = {
    signed:            { cls: 'bg-emerald-900/40 text-emerald-400 border-emerald-500/40', label: 'SIGNED' },
    pending_guardian:  { cls: 'bg-amber-900/40 text-amber-400 border-amber-500/40', label: 'PENDING GUARDIAN' },
    declined:          { cls: 'bg-red-900/40 text-red-400 border-red-500/40', label: 'DECLINED' },
  };
  const m = map[s] || { cls: 'bg-neutral-800 text-neutral-400 border-neutral-600', label: s.toUpperCase() };
  return <span className={`inline-block font-heading text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>;
};

const GuardianPill = ({ st }) => {
  if (!st) return null;
  const map = {
    pending:  { cls: 'bg-amber-900/40 text-amber-400 border-amber-500/40', Icon: Clock },
    approved: { cls: 'bg-emerald-900/40 text-emerald-400 border-emerald-500/40', Icon: CheckCircle2 },
    declined: { cls: 'bg-red-900/40 text-red-400 border-red-500/40', Icon: XCircle },
  };
  const m = map[st] || map.pending;
  const I = m.Icon;
  return <span className={`inline-flex items-center gap-1 font-heading text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${m.cls}`}>
    <I size={9} /> {st}
  </span>;
};

export default function WaiversAdmin() {
  const [tab, setTab] = useState('list');
  return (
    <div className="p-6 space-y-4 max-w-6xl mx-auto">
      <div className="flex items-center gap-3">
        <ShieldAlert className="text-orange-400" size={22} />
        <h2 className="font-heading text-2xl tracking-widest uppercase">Waivers</h2>
      </div>
      <div className="flex gap-2">
        {[
          { id: 'list', label: 'All Waivers' },
          { id: 'settings', label: 'Waiver Text' },
        ].map(t => (
          <button key={t.id} data-testid={`waivers-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`font-heading text-xs tracking-widest uppercase px-4 py-2 rounded-full border ${tab === t.id ? 'bg-orange-600 text-black border-orange-500' : 'bg-transparent text-muted-foreground border-border hover:border-orange-500/50'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'list' && <WaiverList />}
      {tab === 'settings' && <WaiverSettings />}
    </div>
  );
}

function WaiverList() {
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState({});
  const [resending, setResending] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const r = await axios.get(`${API}/api/admin/waivers`, { headers: authHeaders(), params });
      setWaivers(r.data.waivers || []);
    } catch { toast.error('Failed to load waivers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [status]);

  const counts = useMemo(() => {
    return waivers.reduce((acc, w) => { acc[w.status] = (acc[w.status] || 0) + 1; return acc; }, {});
  }, [waivers]);

  const resendGuardian = async (waiver_id, guest_idx, email) => {
    setResending(`${waiver_id}:${guest_idx}`);
    try {
      await axios.post(`${API}/api/admin/waivers/${waiver_id}/resend-guardian/${guest_idx}`, {}, { headers: authHeaders() });
      toast.success(`Guardian email resent to ${email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally { setResending(''); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-background border border-border rounded-lg px-3 py-1.5">
          <Search size={14} className="text-muted-foreground" />
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Search name / email / ID"
            data-testid="waivers-search"
            className="bg-transparent outline-none text-sm w-48" />
          <button onClick={load} className="text-xs text-orange-500">Go</button>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { v: '',                  l: `All (${waivers.length})` },
            { v: 'signed',            l: `Signed (${counts.signed || 0})` },
            { v: 'pending_guardian',  l: `Pending Guardian (${counts.pending_guardian || 0})` },
            { v: 'declined',          l: `Declined (${counts.declined || 0})` },
          ].map(c => (
            <button key={c.v} onClick={() => setStatus(c.v)} data-testid={`waivers-filter-${c.v || 'all'}`}
              className={`font-heading text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border ${status === c.v ? 'bg-orange-600 text-black border-orange-500' : 'bg-transparent text-muted-foreground border-border hover:border-orange-500/50'}`}>
              {c.l}
            </button>
          ))}
        </div>
        <button onClick={load} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        waivers.length === 0 ? <p className="text-sm text-muted-foreground">No waivers yet.</p> : (
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm" data-testid="waivers-table">
              <thead className="bg-secondary text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-3 py-2 w-8"></th>
                  <th className="text-left px-3 py-2">ID</th>
                  <th className="text-left px-3 py-2">Signed</th>
                  <th className="text-left px-3 py-2">Signer</th>
                  <th className="text-left px-3 py-2">Guests</th>
                  <th className="text-left px-3 py-2">Guardian</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {waivers.map(w => {
                  const isOpen = !!expanded[w.waiver_id];
                  return (
                    <React.Fragment key={w.waiver_id}>
                      <tr className="border-t border-border cursor-pointer hover:bg-secondary/40"
                          onClick={() => setExpanded(x => ({ ...x, [w.waiver_id]: !x[w.waiver_id] }))}
                          data-testid={`waiver-row-${w.waiver_id}`}>
                        <td className="px-3 py-2">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                        <td className="px-3 py-2 font-mono text-xs">{w.waiver_id}</td>
                        <td className="px-3 py-2 text-xs">{w.submitted_at ? new Date(w.submitted_at).toLocaleString() : '-'}</td>
                        <td className="px-3 py-2">
                          <div>{w.signer_name || '—'}</div>
                          <div className="text-[11px] text-muted-foreground">{w.email}</div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 text-xs"><Users size={12} /> {w.guest_count}</span>
                          {w.minor_count > 0 && <span className="ml-2 text-[10px] text-yellow-400">({w.minor_count} minor{w.minor_count === 1 ? '' : 's'})</span>}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {w.minor_count > 0 ? (
                            <div className="flex gap-1 flex-wrap">
                              {w.minors_approved > 0 && <GuardianPill st="approved" />}
                              {w.minors_pending > 0 && <GuardianPill st="pending" />}
                              {w.minors_declined > 0 && <GuardianPill st="declined" />}
                            </div>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3 py-2"><StatusPill status={w.status} /></td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-border bg-black/30">
                          <td colSpan={7} className="px-4 py-3">
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {(w.guests || []).map((g, i) => (
                                  <div key={i} className="rounded-lg border border-border bg-card p-3 space-y-1"
                                    data-testid={`waiver-guest-detail-${w.waiver_id}-${i}`}>
                                    <div className="flex items-center justify-between">
                                      <div className="font-heading text-sm">{g.name}</div>
                                      {g.is_minor && <span className="font-heading text-[9px] tracking-widest uppercase bg-yellow-950/60 border border-yellow-500/40 text-yellow-300 px-1.5 py-0.5 rounded">MINOR</span>}
                                    </div>
                                    {g.is_minor && (
                                      <div className="text-xs space-y-1">
                                        <div className="flex items-center gap-2">
                                          <Mail size={11} className="text-muted-foreground" />
                                          <span className="text-muted-foreground">{g.guardian_email}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <GuardianPill st={g.guardian_status || 'pending'} />
                                          {g.guardian_status === 'approved' && g.guardian_name && (
                                            <span className="text-[11px] text-emerald-300 italic">by {g.guardian_name}</span>
                                          )}
                                        </div>
                                        {g.guardian_responded_at && (
                                          <div className="text-[10px] text-muted-foreground">
                                            {new Date(g.guardian_responded_at).toLocaleString()}
                                          </div>
                                        )}
                                        {(g.guardian_status || 'pending') === 'pending' && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); resendGuardian(w.waiver_id, i, g.guardian_email); }}
                                            disabled={resending === `${w.waiver_id}:${i}`}
                                            data-testid={`waiver-resend-${w.waiver_id}-${i}`}
                                            className="mt-1 inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase bg-orange-600 hover:bg-orange-500 text-black px-2 py-1 rounded-full disabled:opacity-50">
                                            <Send size={9} /> {resending === `${w.waiver_id}:${i}` ? 'Sending…' : 'Resend Email'}
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <div className="text-[10px] text-muted-foreground">
                                IP: <span className="font-mono">{w.ip || '—'}</span> · UA: <span className="font-mono truncate max-w-md inline-block align-middle">{(w.user_agent || '').slice(0, 80)}</span> · Version: {w.legal_version}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function WaiverSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/admin/waiver-settings`, { headers: authHeaders() }).then(r => setCfg(r.data)).catch(() => {});
  }, []);
  if (!cfg) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/admin/waiver-settings`, {
        enabled: !!cfg.enabled,
        legal_text_en: cfg.legal_text_en,
        legal_text_ja: cfg.legal_text_ja,
      }, { headers: authHeaders() });
      setCfg(r.data);
      toast.success('Saved — new signers will see the updated waiver');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4 max-w-3xl">
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input type="checkbox" checked={!!cfg.enabled} onChange={e => setCfg({ ...cfg, enabled: e.target.checked })}
          data-testid="waiver-settings-enabled"
          className="sr-only peer" />
        <span className="w-11 h-6 rounded-full bg-neutral-800 border border-neutral-700 relative transition-colors peer-checked:bg-orange-600 peer-checked:border-orange-400 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5" />
        <span className="font-heading text-xs tracking-widest uppercase">{cfg.enabled ? 'Waivers Enabled' : 'Waivers Disabled'}</span>
      </label>

      <div>
        <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">English Waiver Text</label>
        <textarea value={cfg.legal_text_en} onChange={e => setCfg({ ...cfg, legal_text_en: e.target.value })}
          data-testid="waiver-text-en"
          rows={14}
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-sans outline-none focus:border-orange-500" />
      </div>
      <div>
        <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Japanese Waiver Text</label>
        <textarea value={cfg.legal_text_ja} onChange={e => setCfg({ ...cfg, legal_text_ja: e.target.value })}
          data-testid="waiver-text-ja"
          rows={14}
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-sans outline-none focus:border-orange-500" />
      </div>
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="waiver-settings-save"
          className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-black font-heading tracking-widest uppercase text-xs px-4 py-2 rounded-full disabled:opacity-50">
          <Save size={12} /> {saving ? 'Saving…' : 'Save'}
        </button>
        <span className="text-[10px] text-muted-foreground">Version {cfg.version} · saving bumps the version and stamps new signers with it.</span>
      </div>
    </div>
  );
}
